/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  ListAccessPrincipalsParams,
  TLFoundryAccessPrincipalListResponse,
  TLHTTPValidationError,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * Users and groups a foundry admin can grant access to. Candidates are bounded — at most 100 users and 100 groups matching the search, merged and paged in-process — so total_count is the size of that candidate set, not of the directory, and offsets past it return nothing. Refine the search instead of paging deep.
 * @summary List Access Principals
 */
export const listAccessPrincipals = (
  params?: ListAccessPrincipalsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLFoundryAccessPrincipalListResponse>(
    { url: `/foundry/access-principals`, method: 'GET', params, signal },
    options,
  );
};

export const getListAccessPrincipalsQueryKey = (params?: ListAccessPrincipalsParams) => {
  return ['foundry', 'access-principals', ...(params ? [params] : [])] as const;
};

export const getListAccessPrincipalsQueryOptions = <
  TData = Awaited<ReturnType<typeof listAccessPrincipals>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListAccessPrincipalsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccessPrincipals>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListAccessPrincipalsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listAccessPrincipals>>> = ({ signal }) =>
    listAccessPrincipals(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listAccessPrincipals>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListAccessPrincipalsQueryResult = NonNullable<Awaited<ReturnType<typeof listAccessPrincipals>>>;
export type ListAccessPrincipalsQueryError = ErrorType<TLHTTPValidationError>;

export function useListAccessPrincipals<
  TData = Awaited<ReturnType<typeof listAccessPrincipals>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListAccessPrincipalsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccessPrincipals>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAccessPrincipals>>,
          TError,
          Awaited<ReturnType<typeof listAccessPrincipals>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAccessPrincipals<
  TData = Awaited<ReturnType<typeof listAccessPrincipals>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListAccessPrincipalsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccessPrincipals>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAccessPrincipals>>,
          TError,
          Awaited<ReturnType<typeof listAccessPrincipals>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAccessPrincipals<
  TData = Awaited<ReturnType<typeof listAccessPrincipals>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListAccessPrincipalsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccessPrincipals>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Access Principals
 */

export function useListAccessPrincipals<
  TData = Awaited<ReturnType<typeof listAccessPrincipals>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListAccessPrincipalsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccessPrincipals>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListAccessPrincipalsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Access Principals
 */
export const prefetchListAccessPrincipalsQuery = async <
  TData = Awaited<ReturnType<typeof listAccessPrincipals>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListAccessPrincipalsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccessPrincipals>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListAccessPrincipalsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Access Principals
 */
export const invalidateListAccessPrincipals = async (
  queryClient: QueryClient,
  params?: ListAccessPrincipalsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListAccessPrincipalsQueryKey(params) }, options);

  return queryClient;
};
